import React, { useEffect, useCallback, useMemo, useState } from 'react';
import {
  DndContext,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import Protected from '../auth/Protected';
import './CarouselContainer.css';
import { withContext } from './CurationContext';
import ItemActions from './ItemActions';
import PreviewArticle from '../common/PreviewArticle';

const SortableItem = React.memo(({ item, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.story_id,
  });

  const style = useMemo(() => ({
    transform: CSS.Transform.toString(transform),
    transition,
    padding: 16,
    background: isDragging ? 'lightgray' : '#ffffff',
  }), [transform, transition, isDragging]);

  return (
    <li
      className='item'
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {children}
    </li>
  );
});

const CarouselContainer = ({ context, onNewCurationItemClick }) => {
  const items = useMemo(() => context.items, [context.items]);
  
  const [activeItemId, setActiveItemId] = useState(null);
  const activeItem = useMemo(() => items.find((item) => item.story_id === activeItemId), [items, activeItemId]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  const onDragStart = useCallback((event) => {
    const { active } = event;
    const item = items.find((item) => item.story_id === active.id);
    setActiveItemId(item.story_id);
  }, [items]);

  const onDragEnd = useCallback(
    (event) => {
      const { active, over } = event;

      setActiveItemId(null);

      if (!over || active.id === over.id) return;

      const oldIndex = items.findIndex((item) => item.story_id === active.id);
      const newIndex = items.findIndex((item) => item.story_id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(items, oldIndex, newIndex);
        context.updateState({ items: newItems });
      }
    },
    [items, context]
  );

  const adjustGridColumns = useCallback(() => {
    const container = document.querySelector('.hs');
    if (!container) return;

    container.style.setProperty('--total', items.length + 1);

    setTimeout(() => {
      const count = container.querySelectorAll('.item').length;
      container.style.setProperty('--total', count);
    }, 100);
  }, [items.length]);

  useEffect(() => {
    adjustGridColumns();
  }, [adjustGridColumns]);

  return (
    <div className='Carousel'>
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.story_id)}
          strategy={horizontalListSortingStrategy}
        >
          <ul className='hs full'>
            {items.map((item) => (
              <SortableItem key={item.story_id} item={item}>
                <Protected privilege='curation:publish'>
                  <ItemActions item={item} />
                </Protected>
                <PreviewArticle article={item} vertical />
              </SortableItem>
            ))}
            <Protected privilege="curation:publish">
              <li className="item new-item-btn" onClick={onNewCurationItemClick}>
                +
              </li>
            </Protected>
          </ul>
        </SortableContext>
          <DragOverlay>
            {activeItem ? (
              <div style={{ 
                opacity: 0.8,
              }}>
                <Protected privilege='curation:publish'>
                  <ItemActions item={activeItem} />
                </Protected>
                <PreviewArticle article={activeItem} vertical />
              </div>
            ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default withContext(CarouselContainer);
